import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Alert,
  CircularProgress, Snackbar
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { getUsers, createCardForUser, toggleCardEnabled, toggleUserActive } from '../../api/adminApi';
import { motion } from 'framer-motion';
import { usePage } from '../../context/PageContext';
import CreateUserDialog from '../Auth/CreateUserDialog';
import UsersTable from './UsersTable';

const UserManagement = () => {
    const { setPageTitle } = usePage();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [createUserOpen, setCreateUserOpen] = useState(false);
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        setPageTitle('User Management');
    }, [setPageTitle]);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getUsers();
            setUsers(data);
            setError('');
        } catch (err) {
            setError('Failed to fetch users.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleCreateCard = async (userId) => {
        try {
            await createCardForUser(userId);
            setFeedback({ open: true, message: 'New card assigned successfully!', severity: 'success' });
            loadUsers();
        } catch (err) {
            setFeedback({ open: true, message: 'Failed to assign card.', severity: 'error' });
        }
    };

    const handleToggleCardEnabled = async (cardId) => {
        try {
            await toggleCardEnabled(cardId);
            loadUsers();
        } catch (err) {
            setFeedback({ open: true, message: 'Failed to toggle card status.', severity: 'error' });
        }
    };

    const handleToggleUserActive = async (userId) => {
        try {
            await toggleUserActive(userId);
            loadUsers();
        } catch (err) {
            setFeedback({ open: true, message: 'Failed to toggle user status.', severity: 'error' });
        }
    };
    
    const handleCloseFeedback = () => {
        setFeedback({ ...feedback, open: false });
    };

    if (loading) return <CircularProgress />;
    
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 4, width: '100%' }}>
                    <Button variant="contained" startIcon={<Add />} onClick={() => setCreateUserOpen(true)}>
                        Create User
                    </Button>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ width: '100%' }}>
                    <UsersTable
                        users={users}
                        loading={loading}
                        maxHeight={null}
                        showActions={true}
                        onToggleUserActive={handleToggleUserActive}
                        onToggleCardEnabled={handleToggleCardEnabled}
                    />
                </Box>
                
                <CreateUserDialog 
                    open={createUserOpen} 
                    onClose={() => setCreateUserOpen(false)} 
                    onUserCreated={() => {
                        setFeedback({ open: true, message: 'User created successfully!', severity: 'success' });
                        loadUsers();
                    }}
                />

                <Snackbar open={feedback.open} autoHideDuration={6000} onClose={handleCloseFeedback}>
                    <Alert onClose={handleCloseFeedback} severity={feedback.severity} sx={{ width: '100%' }}>
                        {feedback.message}
                    </Alert>
                </Snackbar>
            </Box>
        </motion.div>
    );
};

export default UserManagement; 